import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Users, Phone, Mail, MapPin, Wrench, UserPlus, FileText, Plus,
  ChevronDown, ChevronUp, ArrowRight, Check, Target, Trash2, Globe,
  AlertTriangle, ClipboardList, Send, CalendarDays, Search, Volume2,
  Camera, Video, Headphones, Monitor, MessageSquare,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const statusConfig = {
  new: { label: "Nouveau", color: "bg-[#0099CC]/10 text-[#0099CC]" },
  contacted: { label: "Contacte", color: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  qualified: { label: "Qualifie", color: "bg-[#10B981]/10 text-[#10B981]" },
  won: { label: "Gagne", color: "bg-[#22C55E]/10 text-[#22C55E]" },
  lost: { label: "Perdu", color: "bg-[#EF4444]/10 text-[#EF4444]" },
};

/* ── Category system for leads ── */
const CATEGORY_CONFIG = {
  event_center:   { label: "Event Center",           color: "bg-[#F59E0B]/10 text-[#F59E0B]",  icon: CalendarDays },
  site_web:       { label: "Site web / Application",  color: "bg-[#0099CC]/10 text-[#0099CC]",  icon: Monitor },
  seo:            { label: "SEO",                      color: "bg-[#10B981]/10 text-[#10B981]",  icon: Search },
  sonorisation:   { label: "Sonorisation",             color: "bg-[#F59E0B]/10 text-[#F59E0B]",  icon: Volume2 },
  reportage:      { label: "Reportage",                color: "bg-[#8B5CF6]/10 text-[#8B5CF6]",  icon: Camera },
  regie:          { label: "Regie audiovisuelle",      color: "bg-[#EC4899]/10 text-[#EC4899]",  icon: Video },
  captation:      { label: "Captation",                color: "bg-[#06B6D4]/10 text-[#06B6D4]",  icon: Headphones },
  contact:        { label: "Contact general",          color: "bg-[#64748b]/10 text-[#94A3B8]",  icon: MessageSquare },
};

function categoriseLead(lead) {
  const src = (lead.source || "").toLowerCase();
  const ptype = (lead.project_type || "").toLowerCase();

  // Specific services first (before generic "evenementiel" check)
  if (src.includes("sonorisation") || ptype.includes("sonorisation")) return "sonorisation";
  if (src.includes("reportage") || ptype.includes("reportage"))       return "reportage";
  if (src.includes("regie") || src.includes("régie") || ptype.includes("regie")) return "regie";
  if (src.includes("captation") || ptype.includes("captation"))       return "captation";

  // Event Center: explicit event source or generic "evenementiel"
  if (src.startsWith("evenement:") || src.startsWith("événement:"))   return "event_center";
  if (src.includes("evenementiel") || src.includes("événementiel") || ptype.includes("evenementiel")) return "event_center";

  // Digital services
  if (src.includes("seo") || src.includes("marketing") || ptype.includes("seo") || src === "audit_seo") return "seo";
  if (src.includes("web") || src.includes("site") || src.includes("application") || ptype.includes("site") || ptype.includes("application") || ptype.includes("e-commerce") || ptype.includes("ecommerce")) return "site_web";

  return "contact";
}

/* Filter-tab groupings */
const FILTER_TYPES = {
  all:          { label: "Tous",          matchFn: () => true },
  event_center: { label: "Event Center", matchFn: (cat) => cat === "event_center" },
  devis:        { label: "Devis",        matchFn: (cat) => ["site_web","seo","sonorisation","reportage","regie","captation"].includes(cat) },
  contacts:     { label: "Contacts",     matchFn: (cat) => cat === "contact" },
};

export default function LeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [expandedLead, setExpandedLead] = useState(null);
  const [converting, setConverting] = useState(null);
  const [creatingProposal, setCreatingProposal] = useState(null);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // null = batch, "all" = purge, or lead obj
  const [deleting, setDeleting] = useState(false);
  const [showQuestionnaireDialog, setShowQuestionnaireDialog] = useState(false);
  const [qName, setQName] = useState("");
  const [qEmail, setQEmail] = useState("");
  const [qLeadId, setQLeadId] = useState(null);
  const [sendingQ, setSendingQ] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewLeadDialog, setShowNewLeadDialog] = useState(false);
  const [newLead, setNewLead] = useState({
    name: "", email: "", phone: "", city: "", project_type: "site_web", source: "manuel", notes: "", client_type: "standard"
  });
  const [creatingLead, setCreatingLead] = useState(false);
  const [eventOrgCount, setEventOrgCount] = useState(null);

  useEffect(() => { fetchLeads(); fetchEventOrgCount(); }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data);
    } catch {
      toast.error("Erreur lors du chargement des leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchEventOrgCount = async () => {
    try {
      const res = await axios.get(`${API}/event/admin/organizations`);
      if (Array.isArray(res.data)) setEventOrgCount(res.data.length);
    } catch { /* non-blocking */ }
  };

  const updateStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status: newStatus });
      toast.success(`Statut mis a jour: ${statusConfig[newStatus]?.label}`);
      fetchLeads();
    } catch {
      toast.error("Erreur lors de la mise a jour");
    }
  };

  const convertToClient = async (lead) => {
    setConverting(lead.id);
    try {
      const res = await axios.post(`${API}/leads/${lead.id}/convert-to-client`);
      toast.success(`Lead converti en client: ${res.data.client?.company_name}`);
      setShowConvertDialog(false);
      setSelectedLead(null);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de conversion");
    } finally {
      setConverting(null);
    }
  };

  const createProposalFromLead = async (lead) => {
    setCreatingProposal(lead.id);
    try {
      await axios.post(`${API}/leads/${lead.id}/create-proposal`, {
        project_name: `${lead.project_type} - ${lead.name}`,
        project_type: lead.project_type,
      });
      toast.success("Proposition creee ! Redirection...");
      setTimeout(() => navigate("/app/propositions"), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de creation");
    } finally {
      setCreatingProposal(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (deleteTarget === "all") {
        await axios.delete(`${API}/leads`);
        toast.success("Tous les leads ont ete supprimes");
      } else if (deleteTarget && deleteTarget.id) {
        await axios.delete(`${API}/leads/${deleteTarget.id}`);
        toast.success("Lead supprime");
      } else {
        await axios.post(`${API}/leads/delete-batch`, { ids: [...selectedIds] });
        toast.success(`${selectedIds.size} lead(s) supprime(s)`);
        setSelectedIds(new Set());
      }
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de suppression");
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(l => l.id)));
    }
  };

  const sendQuestionnaire = async (e) => {
    e.preventDefault();
    if (!qName || !qEmail) { toast.error("Nom et email requis"); return; }
    setSendingQ(true);
    try {
      await axios.post(`${API}/project-questionnaires/send`, { name: qName, email: qEmail, lead_id: qLeadId });
      toast.success(`Questionnaire envoye a ${qEmail}`);
      setShowQuestionnaireDialog(false);
      setQName(""); setQEmail(""); setQLeadId(null);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur envoi questionnaire");
    } finally { setSendingQ(false); }
  };

  const openQuestionnaireFor = (lead) => {
    setQName(lead.name);
    setQEmail(lead.email);
    setQLeadId(lead.id);
    setShowQuestionnaireDialog(true);
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    if (!newLead.name || !newLead.email || !newLead.phone) {
      toast.error("Nom, email et telephone requis");
      return;
    }
    setCreatingLead(true);
    try {
      await axios.post(`${API}/leads`, newLead);
      toast.success(`Lead "${newLead.name}" cree avec succes`);
      setShowNewLeadDialog(false);
      setNewLead({ name: "", email: "", phone: "", city: "", project_type: "site_web", source: "manuel", notes: "" });
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de creation");
    } finally {
      setCreatingLead(false);
    }
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      const matchStatus = filterStatus === "all" || l.status === filterStatus;
      const cat = categoriseLead(l);
      const matchType = filterType === "all" || FILTER_TYPES[filterType]?.matchFn(cat);
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || 
        (l.name || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.phone || "").toLowerCase().includes(q) ||
        (l.city || "").toLowerCase().includes(q) ||
        (l.project_type || "").toLowerCase().includes(q) ||
        (l.source || "").toLowerCase().includes(q);
      return matchStatus && matchType && matchSearch;
    });
  }, [leads, filterStatus, filterType, searchQuery]);

  const counts = useMemo(() => {
    const c = { all: leads.length };
    Object.keys(statusConfig).forEach(k => { c[k] = leads.filter(l => l.status === k).length; });
    return c;
  }, [leads]);

  const typeCounts = useMemo(() => {
    const tc = { all: leads.length };
    Object.keys(FILTER_TYPES).forEach(k => {
      if (k === "all") return;
      tc[k] = leads.filter(l => FILTER_TYPES[k].matchFn(categoriseLead(l))).length;
    });
    return tc;
  }, [leads]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leads-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Leads</h1>
          <p className="text-[#94A3B8] mt-1">
            {leads.length} lead{leads.length > 1 ? "s" : ""} &middot; Pipeline commercial
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selectedIds.size > 0 && (
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setShowDeleteDialog(true); }}
              className="border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10" data-testid="delete-selected-btn">
              <Trash2 className="h-4 w-4 mr-1.5" />Supprimer ({selectedIds.size})
            </Button>
          )}
          {leads.length > 0 && (
            <Button variant="outline" onClick={() => { setDeleteTarget("all"); setShowDeleteDialog(true); }}
              className="border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10" data-testid="delete-all-leads-btn">
              <Trash2 className="h-4 w-4 mr-1.5" />Vider tout
            </Button>
          )}
          <Button variant="outline" onClick={async () => {
            try { await axios.post(`${API}/leads/score-all`); toast.success("Scores mis a jour"); fetchLeads(); } catch { toast.error("Erreur"); }
          }} className="border-[#2A3441] text-[#F59E0B] hover:text-[#F59E0B]" data-testid="score-all-btn">
            <Target className="h-4 w-4 mr-1.5" />Scorer
          </Button>
          <Button variant="outline" onClick={fetchLeads}
            className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="refresh-leads-btn">
            Rafraichir
          </Button>
          <Button onClick={() => { setQName(""); setQEmail(""); setQLeadId(null); setShowQuestionnaireDialog(true); }}
            className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="send-questionnaire-btn">
            <ClipboardList className="h-4 w-4 mr-1.5" />Questionnaire
          </Button>
          <Button onClick={() => setShowNewLeadDialog(true)}
            className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="new-lead-btn">
            <Plus className="h-4 w-4 mr-1.5" />Nouveau Lead
          </Button>
        </div>
      </div>

      {/* Event Center org summary banner */}
      {eventOrgCount !== null && eventOrgCount > 0 && (
        <button onClick={() => navigate("/app/event-center")}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-[#F59E0B]/20 bg-[#F59E0B]/5 hover:bg-[#F59E0B]/10 transition-colors text-left"
          data-testid="event-center-banner">
          <CalendarDays className="h-5 w-5 text-[#F59E0B] shrink-0" />
          <span className="text-sm text-[#F59E0B] font-medium">{eventOrgCount} demande{eventOrgCount > 1 ? "s" : ""} Event Center</span>
          <span className="text-xs text-[#64748b]">&mdash; gerees dans l'espace Event Center</span>
          <ArrowRight className="h-4 w-4 text-[#F59E0B] ml-auto shrink-0" />
        </button>
      )}

      {/* Search bar */}
      <div className="relative">
        <Input
          placeholder="Rechercher un lead (nom, email, ville, telephone, source...)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-[#151B2B] border-[#2A3441] text-white placeholder:text-[#64748b] pl-10"
          data-testid="search-leads"
        />
        <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
      </div>

      {/* Type filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(FILTER_TYPES).map(([key, ft]) => (
          <Button key={key} variant={filterType === key ? "default" : "outline"} size="sm"
            onClick={() => setFilterType(key)}
            className={filterType === key
              ? (key === "event_center" ? "bg-[#F59E0B] text-white" : "bg-[#0099CC] text-white")
              : "border-[#2A3441] text-[#94A3B8]"}
            data-testid={`filter-type-${key}`}>
            {ft.label} ({typeCounts[key] || 0})
          </Button>
        ))}
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: "all", label: `Tous (${counts.all})` },
          { key: "new", label: `Nouveaux (${counts.new || 0})` },
          { key: "contacted", label: `Contactes (${counts.contacted || 0})` },
          { key: "qualified", label: `Qualifies (${counts.qualified || 0})` },
          { key: "won", label: `Gagnes (${counts.won || 0})` },
          { key: "lost", label: `Perdus (${counts.lost || 0})` },
        ].map(f => (
          <Button key={f.key} variant={filterStatus === f.key ? "default" : "outline"} size="sm"
            onClick={() => setFilterStatus(f.key)}
            className={filterStatus === f.key ? "bg-[#0099CC] text-white" : "border-[#2A3441] text-[#94A3B8]"}
            data-testid={`filter-${f.key}`}>{f.label}</Button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <Users className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun lead</h3>
          <p className="text-[#94A3B8]">Les demandes Devis/Audit apparaitront ici</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Select all header */}
          <div className="flex items-center gap-3 px-2">
            <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0}
              onChange={toggleSelectAll}
              className="h-4 w-4 rounded border-[#2A3441] accent-[#0099CC]"
              data-testid="select-all-leads" />
            <span className="text-xs text-[#64748b]">Tout selectionner</span>
          </div>

          {filtered.map((l) => {
            const cfg = statusConfig[l.status] || { label: l.status, color: "bg-[#2A3441] text-[#94A3B8]" };
            const date = l.created_at ? new Date(l.created_at).toLocaleDateString("fr-FR") : "";
            const isExpanded = expandedLead === l.id;
            const isWon = l.status === "won";
            const isSelected = selectedIds.has(l.id);
            const cat = categoriseLead(l);
            const catCfg = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.contact;
            const CatIcon = catCfg.icon;

            return (
              <div key={l.id} className={`bg-[#151B2B] border rounded-xl overflow-hidden transition-colors ${isSelected ? "border-[#0099CC]/50" : "border-[#2A3441] hover:border-[#0099CC]/30"}`}
                data-testid={`lead-card-${l.id}`}>
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(l.id)}
                      className="h-4 w-4 mt-1 rounded border-[#2A3441] accent-[#0099CC] flex-shrink-0"
                      data-testid={`select-lead-${l.id}`} />

                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${cfg.color.split(' ')[0]}`}>
                      <Users className={`h-5 w-5 ${cfg.color.split(' ')[1]}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-semibold">{l.name}</p>
                        <Badge className={`${cfg.color} border-0 text-xs`}>{cfg.label}</Badge>
                        <Badge className={`${catCfg.color} border-0 text-xs flex items-center gap-1`} data-testid={`lead-category-${l.id}`}>
                          <CatIcon className="h-3 w-3" />{catCfg.label}
                        </Badge>
                        {l.lead_score != null && (
                          <Badge className={`border-0 text-xs ${l.lead_score >= 70 ? "bg-[#10B981]/10 text-[#10B981]" : l.lead_score >= 40 ? "bg-[#F59E0B]/10 text-[#F59E0B]" : "bg-[#EF4444]/10 text-[#EF4444]"}`} data-testid={`lead-score-${l.id}`}>
                            <Target className="h-3 w-3 mr-0.5" />{l.lead_score}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-[#64748b] flex-wrap">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{l.email}</span>
                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{l.phone}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{l.city}</span>
                        <span className="flex items-center gap-1"><Wrench className="h-3 w-3" />{l.project_type}</span>
                        <span className="flex items-center gap-1"><Globe className="h-3 w-3" />{l.source || "site web"}</span>
                        <span>{date}</span>
                      </div>
                    </div>

                    {/* Delete + Toggle */}
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm"
                        onClick={() => { setDeleteTarget(l); setShowDeleteDialog(true); }}
                        className="text-[#64748b] hover:text-[#EF4444] hover:bg-[#EF4444]/10"
                        data-testid={`delete-lead-${l.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm"
                        onClick={() => setExpandedLead(isExpanded ? null : l.id)}
                        className="text-[#94A3B8] hover:text-white"
                        data-testid={`expand-lead-${l.id}`}>
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Quick actions row */}
                  <div className="mt-3 flex gap-2 justify-end flex-wrap">
                    <div className="w-[160px]">
                      <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                        <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white h-8 text-xs" data-testid={`status-select-${l.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#151B2B] border-[#2A3441]">
                          {Object.entries(statusConfig).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {!isWon && (
                      <>
                        <Button size="sm" variant="outline"
                          onClick={() => openQuestionnaireFor(l)}
                          className="border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 h-8 text-xs"
                          data-testid={`questionnaire-lead-${l.id}`}>
                          <ClipboardList className="h-3 w-3 mr-1" />Questionnaire
                        </Button>
                        <Button size="sm" variant="outline"
                          onClick={() => { setSelectedLead(l); setShowConvertDialog(true); }}
                          disabled={converting === l.id}
                          className="border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/10 h-8 text-xs"
                          data-testid={`convert-lead-${l.id}`}>
                          {converting === l.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <UserPlus className="h-3 w-3 mr-1" />}
                          Convertir en client
                        </Button>
                        <Button size="sm"
                          onClick={() => createProposalFromLead(l)}
                          disabled={creatingProposal === l.id}
                          className="bg-[#0099CC] hover:bg-[#0088B5] text-white h-8 text-xs"
                          data-testid={`create-proposal-lead-${l.id}`}>
                          {creatingProposal === l.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileText className="h-3 w-3 mr-1" />}
                          Creer une proposition
                        </Button>
                      </>
                    )}
                    {isWon && (
                      <Badge className="bg-[#22C55E]/10 text-[#22C55E] border-0 text-xs flex items-center gap-1">
                        <Check className="h-3 w-3" /> Client converti
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-[#2A3441] bg-[#0B0F19]/50 p-5 space-y-3 animate-fade-in">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-[#64748b] mb-1">Contact</p>
                        <p className="text-white">{l.name}</p>
                        <p className="text-[#94A3B8]">{l.email}</p>
                        <p className="text-[#94A3B8]">{l.phone}</p>
                      </div>
                      <div>
                        <p className="text-[#64748b] mb-1">Projet</p>
                        <p className="text-white">{l.project_type}</p>
                        <p className="text-[#94A3B8]">{l.city}</p>
                        <Badge className={`${catCfg.color} border-0 text-xs mt-1 inline-flex items-center gap-1`}>
                          <CatIcon className="h-3 w-3" />{catCfg.label}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-[#64748b] mb-1">Source & Date</p>
                        <p className="text-white">{l.source || "site web"}</p>
                        <p className="text-[#94A3B8]">{date}</p>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-[#2A3441] flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                        <ArrowRight className="h-4 w-4 text-[#0099CC]" />
                        <span>Lead</span>
                        <ArrowRight className="h-3 w-3" />
                        <span>Proposition</span>
                        <ArrowRight className="h-3 w-3" />
                        <span>Client</span>
                        <ArrowRight className="h-3 w-3" />
                        <span>Projet</span>
                      </div>
                      <Button size="sm" variant="outline"
                        onClick={() => { setDeleteTarget(l); setShowDeleteDialog(true); }}
                        className="border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10 text-xs"
                        data-testid={`delete-lead-detail-${l.id}`}>
                        <Trash2 className="h-3 w-3 mr-1" />Supprimer ce lead
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Convert to Client Confirmation Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] text-white" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle>Convertir en client</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <p className="text-[#94A3B8] text-sm">
                Vous allez convertir ce lead en client. Un compte utilisateur sera automatiquement cree et un email de bienvenue sera envoye.
              </p>
              <div className="bg-[#0B0F19] rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#64748b]">Nom</span>
                  <span className="text-white">{selectedLead.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748b]">Email</span>
                  <span className="text-[#0099CC]">{selectedLead.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748b]">Telephone</span>
                  <span className="text-white">{selectedLead.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748b]">Projet</span>
                  <span className="text-white">{selectedLead.project_type}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}
              className="border-[#2A3441] text-[#94A3B8]" data-testid="cancel-convert-btn">Annuler</Button>
            <Button onClick={() => selectedLead && convertToClient(selectedLead)}
              disabled={converting}
              className="bg-[#10B981] hover:bg-[#0d9668] text-white" data-testid="confirm-convert-btn">
              {converting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Convertir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] text-white" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#EF4444]" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#94A3B8] text-sm">
            {deleteTarget === "all"
              ? `Vous allez supprimer TOUS les leads (${leads.length}). Cette action est irreversible.`
              : deleteTarget?.id
                ? `Supprimer le lead "${deleteTarget.name}" ?`
                : `Supprimer ${selectedIds.size} lead(s) selectionne(s) ?`}
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeleteTarget(null); }}
              className="border-[#2A3441] text-[#94A3B8]" data-testid="cancel-delete-btn">Annuler</Button>
            <Button onClick={handleDelete} disabled={deleting}
              className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="confirm-delete-btn">
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Questionnaire Dialog */}
      <Dialog open={showQuestionnaireDialog} onOpenChange={setShowQuestionnaireDialog}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] text-white" data-testid="questionnaire-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[#8B5CF6]" />
              Envoyer un questionnaire projet
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={sendQuestionnaire} className="space-y-4">
            <p className="text-[#94A3B8] text-sm">
              Un lien vers un questionnaire intelligent sera envoye au prospect. Ses reponses seront analysees par l'IA pour generer un cahier des charges.
            </p>
            <div className="space-y-1.5">
              <Label className="text-[#94A3B8] text-xs">Nom du prospect</Label>
              <Input value={qName} onChange={e => setQName(e.target.value)}
                placeholder="Jean Dupont"
                className="bg-[#0B0F19] border-[#2A3441] text-white"
                data-testid="q-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#94A3B8] text-xs">Email du prospect</Label>
              <Input type="email" value={qEmail} onChange={e => setQEmail(e.target.value)}
                placeholder="prospect@email.com"
                className="bg-[#0B0F19] border-[#2A3441] text-white"
                data-testid="q-email-input" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowQuestionnaireDialog(false)}
                className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button type="submit" disabled={sendingQ}
                className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white" data-testid="confirm-send-q-btn">
                {sendingQ ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Envoyer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Lead Dialog */}
      <Dialog open={showNewLeadDialog} onOpenChange={setShowNewLeadDialog}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441] text-white max-w-lg" data-testid="new-lead-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#10B981]" />
              Nouveau Lead
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLead} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Nom *</Label>
                <Input value={newLead.name} onChange={e => setNewLead({...newLead, name: e.target.value})}
                  placeholder="Jean Dupont" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="lead-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Email *</Label>
                <Input type="email" value={newLead.email} onChange={e => setNewLead({...newLead, email: e.target.value})}
                  placeholder="email@example.com" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="lead-email-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Telephone *</Label>
                <Input value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})}
                  placeholder="06 12 34 56 78" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="lead-phone-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Ville</Label>
                <Input value={newLead.city} onChange={e => setNewLead({...newLead, city: e.target.value})}
                  placeholder="Paris" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="lead-city-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Type de service</Label>
                <select value={newLead.project_type} onChange={e => setNewLead({...newLead, project_type: e.target.value})}
                  className="w-full h-9 px-3 rounded-md bg-[#0B0F19] border border-[#2A3441] text-white text-sm" data-testid="lead-type-select">
                  <optgroup label="Digital">
                    <option value="site_web">Site Web</option>
                    <option value="seo">SEO / Referencement</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="e-commerce">E-commerce</option>
                    <option value="application">Application</option>
                    <option value="social_media">Reseaux Sociaux</option>
                  </optgroup>
                  <optgroup label="Evenementiel">
                    <option value="sonorisation">Sonorisation</option>
                    <option value="reportage">Reportage photo & video</option>
                    <option value="regie">Regie audiovisuelle</option>
                    <option value="captation">Captation audio</option>
                    <option value="evenementiel">Evenementiel global</option>
                  </optgroup>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Source</Label>
                <select value={newLead.source} onChange={e => setNewLead({...newLead, source: e.target.value})}
                  className="w-full h-9 px-3 rounded-md bg-[#0B0F19] border border-[#2A3441] text-white text-sm" data-testid="lead-source-select">
                  <option value="manuel">Saisie manuelle</option>
                  <option value="telephone">Telephone</option>
                  <option value="Formulaire devis — Sonorisation evenementielle">Devis Sonorisation</option>
                  <option value="Formulaire devis — Reportage photo & video">Devis Reportage</option>
                  <option value="Formulaire devis — Regie audiovisuelle">Devis Regie AV</option>
                  <option value="Formulaire devis — Captation audio">Devis Captation</option>
                  <option value="Formulaire devis — Evenementiel global">Devis Evenementiel</option>
                  <option value="evenement">Evenement</option>
                  <option value="recommandation">Recommandation</option>
                  <option value="site_web">Site web</option>
                  <option value="reseaux_sociaux">Reseaux sociaux</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Type de client</Label>
                <select value={newLead.client_type} onChange={e => setNewLead({...newLead, client_type: e.target.value})}
                  className="w-full h-9 px-3 rounded-md bg-[#0B0F19] border border-[#2A3441] text-white text-sm" data-testid="lead-client-type-select">
                  <option value="standard">Standard (prestation)</option>
                  <option value="agency">Agence (CRM white-label)</option>
                </select>
              </div>
              <div />
            </div>
            <div className="space-y-1">
              <Label className="text-[#94A3B8] text-xs">Notes</Label>
              <textarea value={newLead.notes} onChange={e => setNewLead({...newLead, notes: e.target.value})}
                placeholder="Notes supplementaires..." rows={3}
                className="w-full px-3 py-2 rounded-md bg-[#0B0F19] border border-[#2A3441] text-white text-sm resize-none" data-testid="lead-notes-input" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowNewLeadDialog(false)}
                className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button type="submit" disabled={creatingLead}
                className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="confirm-create-lead-btn">
                {creatingLead ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Creer le lead
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
